import { useCallback, useMemo } from 'react';
import { type ContextualMenuProps } from '@adc/parallax-component-library';
import { useLocation, useNavigate } from '@tanstack/react-router';

import { AppMenuItemIcon } from '~/components/navigation/AppMenuItemIcon';
import { useAuthState } from '~/features/auth/useAuthState';
import {
  getAppMenuItemsByRole,
  getNavigationItemsByRole,
} from '~/features/navigation/utils';

export const useNavigationItems = () => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { isAuthenticated, userInfo } = useAuthState();

  const userRole = userInfo?.role ?? undefined;

  const appMenuItems = useMemo(() => {
    if (!isAuthenticated || !userRole) {
      return [];
    }

    return getAppMenuItemsByRole(userRole).map(
      (i) =>
        ({
          ...i,
          iconStart: <AppMenuItemIcon />,
        }) satisfies ContextualMenuProps['items'][number],
    );
  }, [userRole, isAuthenticated]);

  const appMenuItemSelected = useMemo(
    () =>
      appMenuItems.findIndex(({ id }) => {
        const navItems = getNavigationItemsByRole({
          role: userRole,
          navigationSectionId: id,
        });

        // Note: we are using `.startsWith` instead of equality check for cases like `/department` and `/department/{id}`
        return navItems.some(({ path }) => pathname.startsWith(path));
      }),
    [appMenuItems, userRole, pathname],
  );

  const currentlySelectedAppMenuItemId = appMenuItems[appMenuItemSelected]?.id;

  const navigationItems = useMemo(() => {
    if (!isAuthenticated || !userRole) {
      return [];
    }

    return getNavigationItemsByRole({
      role: userRole,
      navigationSectionId: currentlySelectedAppMenuItemId,
    });
  }, [isAuthenticated, userRole, currentlySelectedAppMenuItemId]);

  const handleSelectAppMenuItem = useCallback(
    (itemIndex: number) => {
      const newPath = getNavigationItemsByRole({
        role: userRole,
        navigationSectionId: appMenuItems[itemIndex]?.id,
      })[0]?.path;

      if (newPath) {
        void navigate({ to: newPath });
      }
    },
    [navigate, userRole, appMenuItems],
  );

  return {
    navigationItems,
    appMenuItems,
    handleSelectAppMenuItem,
    appMenuItemSelected,
  };
};
